<template>
<div class="row">

	<div class="col">
		<b-form @submit.prevent="submitForm"
		        class="mb-3">

			<action-buttons ref="actionButtons"
			                class="pull-right"
			                @save="submitForm"
			                @save-and-back="submitFormAndBack"
			                @back="goBack"
			                @delete="deleteBid"
			                :disable-next="true"
			                :disable-previous="true"
			                :disable-save-and-new="true"
			                :isDeleting="isDeleting"
			                :delete-name="$t('bid.thisBid')">
			</action-buttons>

			<h1 class="display-4">{{ $t('bid.edit') }}</h1>

			<b-alert v-if="formError"
			         show
			         variant="warning">
				<span v-for="errors in formError.errors">
						<span v-for="error in errors">{{ error }}<br /></span>
				</span>
			</b-alert>

			<partial-loader :loading="isLoading"></partial-loader>

			<div v-if="!isLoading">
				<b-jumbotron class="mb-3 p-4">
					<h2 v-if="bid.item">{{ $t('bid.itemBid') }}
						<router-link :to="{ name: 'item', params: { itemId: bid.item.id } }">
							{{ bid.item.seller.code }}-{{ bid.item.number }} {{ bid.item.title }}
						</router-link>
					</h2>

					<p>
						<small>Aangemaakt op {{ bid.created_at | bigDate }}</small>
					</p>

					<h2 v-if="bid.bid_number"
					    class="font-italic">
						{{ $t('bid.bidNumber') }}
						<router-link :to="{ name: 'auctionBuyer', params: { bidNumber: bid.bid_number } }">
							{{ bid.bid_number }}
						</router-link>
					</h2>

					<p v-if="bid.buyer == null"
					   class="font-italic">
						{{ $t('bid.noBuyerForBidnumer') }}
					</p>

					<div v-else>
						<person-info :person="bid.buyer.person"></person-info>
					</div>
				</b-jumbotron>

				<div class="row">
					<div class="col-sm-12 col-md-4 col-lg-3">
						<b-form-group label="Hamerbedrag:">
							<b-input-group>
								<b-input-group-prepend is-text>
									&euro;
								</b-input-group-prepend>
								
								<b-form-input type="number"
								              step="any"
											  autocomplete="off"
								              v-model="form.price"></b-form-input>
							</b-input-group>
						</b-form-group>
					</div>
				</div>

				<div class="row">
					<div class="col-sm-12 col-md-4 col-lg-3">
						<b-form-group label="Biednummer:">
							<b-input-group>
								<b-form-input type="text"
											  autocomplete="off"
								              v-model="form.bid_number"></b-form-input>
							</b-input-group>
						</b-form-group>
					</div>
				</div>
			</div>
		</b-form>
	</div>
</div>
</template>

<script>
// imports
import AutocompletePerson from '@/components/elements/AutocompletePerson';
import ActionButtons from '@/components/elements/Edit/ActionButtons';
import PartialLoader from '@/components/elements/PartialLoader';
import PersonInfo from '@/components/views/Person/PersonInfo';
import { flashSuccess, flashDanger } from '@/helpers/flash-message-helper';
import { pick, isNil } from 'lodash';
import { replaceEmptyByNull } from '@/helpers/object-helper';
import { createNamespacedHelpers } from 'vuex';
import { bigDate } from '@/filters/date-filters';
import { setTitle } from '@/helpers/page-helper';

// define variables
const { mapGetters, mapActions } = createNamespacedHelpers('bidForm');

export default {
	components: {
		AutocompletePerson,
		ActionButtons,
		PartialLoader,
		PersonInfo
	},
	filters: {
		bigDate
	},
	data() {
		return {
			form: {},
		}
	},
	computed: {
		...mapGetters([
			'bid',
			'isFetching',
			'isUpdating',
			'isDeleting',
			'formError',
			'deleteError'
		]),
		isLoading() {
			return (this.isFetching || this.isUpdating);
		}
	},
	watch: {
		'$route': 'initialize',
		'bid': 'fillForm'
	},
	methods: {
		...mapActions({
			getBid: 'getBid',
			updateBid: 'updateBid',
			deleteAction: 'deleteBid',
			resetAll: 'resetAll'
		}),

		/**
		 * Initialize the component
		 */
		initialize() {
			// reset form
			this.form = {};

			this.resetAll();
			this.getBid(this.$route.params);
		},

		/**
		 * Fill form with the initial bid values
		 */
		fillForm() {

			this.form = pick(this.bid, ['price', 'bid_number']);

			if (!isNil(this.bid.item)) {
				setTitle(this.$t('bid.itemBidTitle', { title: `${this.bid.item.seller.code}-${this.bid.item.number}` }));
			}

		},

		deleteBid() {
			this.deleteAction({
					itemId: this.bid.item.id,
					bidId: this.bid.id
				})
				.then(() => {

					if (!this.deleteError) {

						flashSuccess(this.$t('bid.deleteSuccess'), true);
						this.$router.push({ name: 'bidList' });

					} else {

						this.$refs.actionButtons.hideDeleteModal();
						flashDanger(this.$t('bid.deleteError'));

					}
				})
		},

		/**
		 * Submit Form
		 * @return 	{Promise} 				promise is resolved when bid is updated
		 * @param 	{event} 	e 			submit event
		 * @param 	{Boolean}	nextPage	should the success message show on the next page
		 */
		submitForm(e, nextPage = false) {

			return this.updateBid({
				itemId: this.bid.item.id,
				bidId: this.$route.params.bidId,
				bid: replaceEmptyByNull(this.form)
			}).then(() => {

				if (!this.formError) {

					flashSuccess(this.$t('bid.updateSuccess'), nextPage);

					if (nextPage) {
						this.goBack();
					}
				}
			});
		},

		/**
		 * Submit the form and go back to the bid list
		 */
		submitFormAndBack() {
			this.submitForm(null, true);
		},

		/**
		 * Go back to the bid list
		 */
		goBack() {
			this.$router.push({ name: 'bidList' });
		}
	},
	mounted() {
		this.initialize();
	}
}
</script>

<style lang="scss">
</style>



// WEBPACK FOOTER //
// BidForm.vue?50a67414